import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog.jsx'

import { useViewsListContext } from '@/context-state/ViewsListContext.jsx'
import { useDashboardContext } from '@/context-state/DashboardContext.jsx'
import { useCurrentSpace } from '@/hooks/useCurrentSpace.jsx'
import { handleError } from '@/services/errorHandler.js'
import { AnalyticsService } from '@/services/analytics.js'
import { buildTemplatePayload } from '@/pages/dashboard/views/templates/viewTemplates.js'
import { ViewTemplatePicker } from '@/pages/dashboard/views/ViewTemplatePicker.jsx'

// "New dashboard" entry point — pick a template (creates a view pre-filled with
// the template's name/icon/widgets/scope) or start blank (creates an empty view).
// Either way we navigate to the new view on success.
export const NewViewDialog = ({ isOpen, onClose }) => {

    const { views, createView } = useViewsListContext()
    const { space } = useCurrentSpace()
    const { integrationsContext } = useDashboardContext()
    const navigate = useNavigate()
    const { accountId, spaceId } = useParams()
    const [creating, setCreating] = useState(null)

    const base = `/account/${accountId}/space/${spaceId}/dashboard/creative/views`

    // Default account override (space's first ad account) so widgets render immediately.
    const resolveOverride = () => {
        const firstAccount = space?.adAccounts?.[0]
        if (!firstAccount) {
            return null
        }
        const integrations = integrationsContext?.integrations || []
        const owner = integrations.find(i => i.adAccounts?.some(a => a.id === firstAccount.id))
        return {
            adAccountIds: [firstAccount.id],
            integrationIds: owner?._id ? [owner._id] : [],
        }
    }

    const createAndGo = async (key, payload) => {
        if (creating) {
            return
        }
        setCreating(key)
        try {
            const view = await createView(payload)
            if (view) {
                onClose()
                navigate(`${base}/${view._id}`)
            }
        } catch (e) {
            handleError(e)
            toast.error(e.message)
        } finally {
            setCreating(null)
        }
    }

    const handlePickTemplate = (template) => {
        AnalyticsService.track('view-template-picked', { template: template.key, from: 'new-dialog' })
        createAndGo(template.key, buildTemplatePayload(template, resolveOverride()))
    }

    const handleStartBlank = () => {
        AnalyticsService.track('view-create-clicked', { from: 'new-dialog' })
        const nextNumber = (views?.length || 0) + 1
        createAndGo('blank', { title: `Dashboard #${nextNumber}` })
    }

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-3xl">
                <DialogHeader>
                    <DialogTitle>New dashboard</DialogTitle>
                    <DialogDescription>Start from a template, or build your own from scratch.</DialogDescription>
                </DialogHeader>
                <div className="max-h-[70vh] overflow-y-auto pr-1">
                    <ViewTemplatePicker
                        onPick={handlePickTemplate}
                        onStartBlank={handleStartBlank}
                        busyKey={creating}
                    />
                </div>
            </DialogContent>
        </Dialog>
    )
}
